import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { CalendarDays, Disc3, Images, Ticket, ArrowLeft, LogOut, LayoutDashboard, LogIn, Bell, BellRing } from "lucide-react";
import { useAuth } from "./AuthContext";
import { enableReminders } from "./push";

const tabs = [
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/mixes", label: "Mixes", icon: Disc3 },
  { to: "/gallery", label: "Gallery", icon: Images },
  { to: "/tickets", label: "Tickets", icon: Ticket },
];

export function AppShell() {
  const { user, isOrganizer, logout } = useAuth();
  const nav = useNavigate();
  const [remOn, setRemOn] = useState(localStorage.getItem("remindersOn") === "1");
  const [toast, setToast] = useState("");

  const toggleReminders = async () => {
    setToast("Turning on reminders…");
    const r = await enableReminders();
    if (r.ok) { setRemOn(true); setToast("Reminders on — we'll ping you the day before each party 🎶"); }
    else setToast(r.error);
    setTimeout(() => setToast(""), 4500);
  };

  return (
    <div className="min-h-screen bg-ink-950 text-white pb-24">
      <div className="grain" aria-hidden="true" />
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-ink-950/80 px-4 py-4 backdrop-blur-xl">
        <button onClick={() => nav("/")} data-testid="app-home-btn" className="flex items-center gap-2">
          <ArrowLeft size={18} className="text-zinc-400" />
          <span className="font-oswald text-sm uppercase tracking-[0.2em]">Nou La Baby<span className="text-gold">.</span></span>
        </button>
        <div className="flex items-center gap-3">
          <button onClick={toggleReminders} data-testid="reminders-btn" aria-label="Enable reminders"
            className={remOn ? "text-gold" : "text-zinc-400 hover:text-gold"}>
            {remOn ? <BellRing size={18} /> : <Bell size={18} />}
          </button>
          {isOrganizer && (
            <NavLink to="/organizer" data-testid="nav-organizer" className="flex items-center gap-1.5 border border-gold/40 px-3 py-1.5 text-gold">
              <LayoutDashboard size={14} />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Organizer</span>
            </NavLink>
          )}
          {user ? (
            <button onClick={logout} data-testid="logout-btn" className="text-zinc-400 hover:text-gold"><LogOut size={18} /></button>
          ) : (
            <NavLink to="/login" data-testid="nav-login" className="text-zinc-400 hover:text-gold"><LogIn size={18} /></NavLink>
          )}
        </div>
      </header>

      {toast && (
        <div data-testid="reminders-toast" className="mx-auto max-w-3xl px-4 pt-3">
          <div className="border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold">{toast}</div>
        </div>
      )}

      <main className="mx-auto max-w-3xl px-4 py-6">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-ink-950/90 backdrop-blur-xl">
        <div className="mx-auto grid max-w-3xl grid-cols-4">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`tab-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 transition-colors ${isActive ? "text-gold" : "text-zinc-500"}`
              }
            >
              <t.icon size={20} />
              <span className="font-mono text-[9px] uppercase tracking-[0.15em]">{t.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
